import { isOk, type Result } from "@hightouch/lib/util/result";
import type { Query } from "@cubejs-client/core";
import { useQueries, useQuery } from "react-query";
import type {
  CubeQueryFailure,
  HeldQueryResult,
  InsightsLoad,
  InsightsQueryResult,
  WiredBlend,
  WiredCube,
} from "src/pages/ad-studio/insights/performance/types";
import {
  loadCubeBlend,
  loadCubeQuery,
} from "src/pages/ad-studio/insights/performance/utils/cube-api";

type SubjectAnswer<T> = {
  subject: string;
  result: Result<T, CubeQueryFailure>;
};

const toInsightsQueryResult = <T>(
  answer: SubjectAnswer<T> | undefined,
): InsightsQueryResult<T, CubeQueryFailure> => {
  if (answer === undefined) {
    return { status: "loading" };
  }
  const { result } = answer;
  return isOk(result)
    ? { status: "loaded", value: result.value }
    : { status: "failed", failure: result.error };
};

// react-query hands back the last answer while a new key loads; only the same subject's value stands in for it.
const toHeldQueryResult = <T>({
  answer,
  isPreviousData,
  subject,
}: {
  answer: SubjectAnswer<T> | undefined;
  isPreviousData: boolean;
  subject: string;
}): HeldQueryResult<T, CubeQueryFailure> => {
  if (answer === undefined || !isPreviousData) {
    return toInsightsQueryResult(answer);
  }
  return answer.subject === subject && isOk(answer.result)
    ? { status: "reloading", value: answer.result.value }
    : { status: "loading" };
};

const cubeQueryKey = (query: Query | readonly Query[]) => [
  "insights-cube-query",
  query,
];

export const cubeLoad = <T>({ cube, wire }: WiredCube<T>): InsightsLoad<T> => ({
  subject: cube.subject,
  key: cubeQueryKey(cube.query),
  load: () => loadCubeQuery({ query: cube.query, rows: cube.rows, wire }),
});

export const blendLoad = <T>({
  blend,
  wire,
}: WiredBlend<T>): InsightsLoad<T> => ({
  subject: blend.subject,
  key: cubeQueryKey(blend.queries),
  load: () => loadCubeBlend({ queries: blend.queries, rows: blend.rows, wire }),
});

/** The key and fetch of one load, shared by the hooks and the route loader so a prefetch lands where the hook reads. */
export const insightsQueryOptions = <T>({
  subject,
  key,
  load,
}: InsightsLoad<T>) => ({
  queryKey: [...key],
  queryFn: async (): Promise<SubjectAnswer<T>> => ({
    subject,
    result: await load(),
  }),
});

export function useInsightsQueries<T>(
  loads: readonly InsightsLoad<T>[],
): readonly InsightsQueryResult<T, CubeQueryFailure>[] {
  const answers = useQueries(loads.map(insightsQueryOptions));
  return loads.map((_, index) => toInsightsQueryResult(answers[index]?.data));
}

/** Keeps a control's last values on screen while its key changes, so an open menu does not blank on every pick. */
export function useHeldQueries<T>(
  loads: readonly InsightsLoad<T>[],
): readonly HeldQueryResult<T, CubeQueryFailure>[] {
  const answers = useQueries(
    loads.map((load) => ({
      ...insightsQueryOptions(load),
      keepPreviousData: true,
    })),
  );
  return loads.map(({ subject }, index) => {
    const answer = answers[index];
    return toHeldQueryResult({
      answer: answer?.data,
      isPreviousData: answer?.isPreviousData ?? false,
      subject,
    });
  });
}

export function useHeldQuery<T>(
  load: InsightsLoad<T>,
): HeldQueryResult<T, CubeQueryFailure> {
  const answer = useQuery({
    ...insightsQueryOptions(load),
    keepPreviousData: true,
  });
  return toHeldQueryResult({
    answer: answer.data,
    isPreviousData: answer.isPreviousData,
    subject: load.subject,
  });
}

export function useInsightsQuery<T>(
  load: InsightsLoad<T>,
): InsightsQueryResult<T, CubeQueryFailure> {
  const [result] = useInsightsQueries([load]);
  if (result === undefined) {
    throw new Error("useInsightsQueries returned no result for its load");
  }
  return result;
}
